<script setup>
import { computed, ref } from 'vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
import FormattedContent from './FormattedContent.vue';
import AttachmentChips from 'next/message/chips/AttachmentChips.vue';
import TranslationToggle from 'dashboard/components-next/message/TranslationToggle.vue';
import GroupParticipantName from '../../GroupParticipantName.vue';
import { MESSAGE_TYPES } from '../../constants';
import { useMessageContext } from '../../provider.js';
import { useTranslations } from 'dashboard/composables/useTranslations';

const {
  content,
  attachments,
  contentAttributes,
  messageType,
  templateParams: templateParamsRef,
} = useMessageContext();

const { hasTranslations, translationContent } =
  useTranslations(contentAttributes);

const renderOriginal = ref(false);

const renderContent = computed(() => {
  if (renderOriginal.value) {
    return content.value;
  }

  if (hasTranslations.value) {
    return translationContent.value;
  }

  return content.value;
});

const isTemplate = computed(() => {
  return messageType.value === MESSAGE_TYPES.TEMPLATE;
});

const templateParams = computed(() => {
  return templateParamsRef?.value || null;
});

// Find persisted image attachment to use as template header (permanent S3 URL)
const headerImageAttachment = computed(() => {
  if (!templateParams.value) return null;
  return attachments.value?.find(a => a.fileType === 'image') || null;
});

const templateHeaderImage = computed(() => {
  if (!templateParams.value) return null;

  // Prefer persisted attachment URL (permanent S3) over CDN URLs that expire
  if (headerImageAttachment.value) {
    return headerImageAttachment.value.dataUrl;
  }

  // Fallback to template_params URLs (Meta CDN, may expire)
  const pp = templateParams.value.processed_params || {};
  if (pp.header?.media_url) return pp.header.media_url;
  const comps = templateParams.value.components || [];
  const header = comps.find(c => c.type === 'HEADER' && c.format === 'IMAGE');
  return (
    header?.example?.header_handle?.[0] ||
    header?.example?.header_url?.[0] ||
    null
  );
});

// Filter out the header image from regular attachment chips to avoid duplication
const filteredAttachments = computed(() => {
  if (!headerImageAttachment.value) return attachments.value;
  return (
    attachments.value?.filter(a => a.id !== headerImageAttachment.value.id) ||
    []
  );
});

const templateFooter = computed(() => {
  if (!templateParams.value?.components) return null;
  const footer = templateParams.value.components.find(c => c.type === 'FOOTER');
  return footer?.text || null;
});

const templateButtons = computed(() => {
  if (!templateParams.value?.components) return [];
  const btns = templateParams.value.components.find(c => c.type === 'BUTTONS');
  return btns?.buttons || [];
});

// Interactive message buttons (WhatsApp buttons/list sent via Evolution API)
const interactiveButtons = computed(() => {
  const attrs = contentAttributes.value || {};
  if (attrs.buttons?.length) return attrs.buttons;
  return [];
});

const interactiveSections = computed(() => {
  const attrs = contentAttributes.value || {};
  if (attrs.sections?.length) return attrs.sections;
  return [];
});

const interactiveReply = computed(() => {
  const attrs = contentAttributes.value || {};
  if (attrs.button_reply) {
    return {
      type: 'button',
      text:
        attrs.button_reply.selected_display_text ||
        attrs.button_reply.selected_button_id,
    };
  }
  if (attrs.list_reply) {
    return {
      type: 'list',
      text: attrs.list_reply.title,
      description: attrs.list_reply.description,
    };
  }
  return null;
});

const hasInteractiveContent = computed(() => {
  return (
    interactiveButtons.value.length > 0 ||
    interactiveSections.value.length > 0 ||
    interactiveReply.value !== null
  );
});

const isEmpty = computed(() => {
  return (
    !content.value && !attachments.value?.length && !hasInteractiveContent.value
  );
});

const handleSeeOriginal = () => {
  renderOriginal.value = !renderOriginal.value;
};
</script>

<template>
  <BaseBubble
    class="pb-3 overflow-hidden"
    :class="[
      templateParams ? 'px-0' : 'px-4',
      templateHeaderImage ? 'pt-0' : 'pt-3',
    ]"
    data-bubble-name="text"
  >
    <div class="gap-3 flex flex-col">
      <GroupParticipantName :class="templateParams ? 'px-4' : ''" />

      <!-- Template header image (full-width, like WhatsApp) -->
      <img
        v-if="templateHeaderImage"
        :src="templateHeaderImage"
        class="w-full object-cover rounded-t-lg"
        alt=""
        @error="$event.target.style.display = 'none'"
      />

      <span v-if="isEmpty && !templateParams" class="text-n-slate-11 px-4">
        {{ $t('CONVERSATION.NO_CONTENT') }}
      </span>
      <FormattedContent
        v-if="renderContent"
        :content="renderContent"
        :class="templateParams ? 'px-4' : ''"
      />
      <TranslationToggle
        v-if="hasTranslations"
        class="-mt-3"
        :class="templateParams ? 'px-4' : ''"
        :showing-original="renderOriginal"
        @toggle="handleSeeOriginal"
      />

      <!-- Template footer -->
      <span v-if="templateFooter" class="text-xs text-n-slate-10 px-4">
        {{ templateFooter }}
      </span>

      <!-- Template buttons -->
      <div
        v-if="templateButtons.length > 0"
        class="flex flex-col border-t border-n-weak"
      >
        <div
          v-for="(btn, idx) in templateButtons"
          :key="idx"
          class="text-center py-2 text-sm font-medium text-n-blue-11 border-b border-n-weak last:border-b-0"
        >
          {{ btn.text }}
        </div>
      </div>

      <!-- Interactive buttons (WhatsApp buttons sent/received via Evolution) -->
      <div
        v-if="interactiveButtons.length > 0"
        class="flex flex-col border-t border-n-weak"
      >
        <div
          v-for="(btn, idx) in interactiveButtons"
          :key="'ib-' + idx"
          class="text-center py-2 text-sm font-medium text-n-blue-11 border-b border-n-weak last:border-b-0"
        >
          {{ btn.title }}
        </div>
      </div>

      <!-- Interactive list sections (WhatsApp list sent via Evolution) -->
      <div
        v-if="interactiveSections.length > 0"
        class="flex flex-col border-t border-n-weak"
      >
        <template
          v-for="(section, sIdx) in interactiveSections"
          :key="'is-' + sIdx"
        >
          <div
            v-if="section.title"
            class="px-4 py-1.5 text-xs font-semibold text-n-slate-11 uppercase bg-n-alpha-2"
          >
            {{ section.title }}
          </div>
          <div
            v-for="(row, rIdx) in section.rows"
            :key="'ir-' + sIdx + '-' + rIdx"
            class="px-4 py-2 text-sm text-n-blue-11 border-b border-n-weak last:border-b-0"
          >
            <div class="font-medium">{{ row.title }}</div>
            <div v-if="row.description" class="text-xs text-n-slate-10 mt-0.5">
              {{ row.description }}
            </div>
          </div>
        </template>
      </div>

      <!-- Interactive reply indicator (customer selected a button/list item) -->
      <div v-if="interactiveReply && !renderContent" class="px-4">
        <div
          class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-n-alpha-3 text-sm"
        >
          <span class="text-n-slate-12 font-medium">{{
            interactiveReply.text
          }}</span>
        </div>
        <div
          v-if="interactiveReply.description"
          class="text-xs text-n-slate-10 mt-1"
        >
          {{ interactiveReply.description }}
        </div>
      </div>

      <AttachmentChips
        :attachments="filteredAttachments"
        class="gap-2"
        :class="templateParams ? 'px-4' : ''"
      />
      <template v-if="isTemplate">
        <div
          v-if="contentAttributes.submittedEmail"
          class="px-2 py-1 rounded-lg bg-n-alpha-3 mx-4"
        >
          {{ contentAttributes.submittedEmail }}
        </div>
      </template>
    </div>
  </BaseBubble>
</template>

<style>
p:last-child {
  margin-bottom: 0;
}
</style>
